import { Lock, ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { hasFeature, upgradeTargetFor, tierLabel } from "@/lib/tier";

/**
 * LockedFeatureCard — calm upgrade gate for features above the user's tier.
 *
 * Renders nothing when the user already has access to `feature`. Otherwise
 * shows a tinted card with explainer copy + "Upgrade to {tier}" CTA. Designed
 * to *educate*, not to nag — no aggressive modal, no scarcity copy.
 *
 * Props:
 *   feature      string — feature id from tier_features.py
 *   title        string — short headline ("Deal comparison")
 *   description  string — 1–2 sentence calm value explanation
 *   compact      bool   — render as inline pill instead of card (default false)
 *   children     ReactNode — optional locked-preview content (rendered greyed out
 *                            with a soft overlay)
 */
export default function LockedFeatureCard({
    feature,
    title,
    description,
    compact = false,
    children,
}) {
    const { user } = useAuth();
    if (!feature) return null;

    // If the user already has access, render the children unmodified — this
    // makes the component a thin pass-through for any caller that wraps a
    // gated section.
    if (hasFeature(user, feature)) {
        return children || null;
    }

    const target = upgradeTargetFor(user, feature) || "investor_pro";
    const targetLabel = tierLabel(target) || "Investor Pro";
    const ctaCopy = `Upgrade to ${targetLabel}`;

    if (compact) {
        return (
            <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bl-teal-tint/60 text-bl-primary text-xs"
                data-testid={`locked-feature-pill-${feature}`}
            >
                <Lock className="w-3.5 h-3.5 shrink-0" aria-hidden />
                <span>{title || "Unlock with"}</span>
                <Link
                    to="/pricing"
                    className="font-medium hover:underline underline-offset-2"
                    data-testid={`locked-feature-cta-${feature}`}
                >
                    {targetLabel}
                </Link>
            </div>
        );
    }

    return (
        <div
            className="bl-card p-5 md:p-6 relative overflow-hidden"
            data-testid={`locked-feature-${feature}`}
        >
            {children && (
                <div
                    className="absolute inset-0 pointer-events-none bl-fade-overlay-surface"
                    aria-hidden
                />
            )}
            {children && (
                <div
                    className="opacity-40 pointer-events-none select-none mb-4"
                    aria-hidden
                >
                    {children}
                </div>
            )}
            <div className="relative">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-bl-teal-tint flex items-center justify-center">
                        <Lock className="w-4 h-4 text-bl-primary" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-display font-semibold text-bl text-lg">
                                {title}
                            </h3>
                            <span className="bl-badge bl-badge-neutral inline-flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                {targetLabel}
                            </span>
                        </div>
                        {description && (
                            <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                {description}
                            </p>
                        )}
                        <Link
                            to="/pricing"
                            className="bl-btn-primary mt-4 inline-flex"
                            data-testid={`locked-feature-cta-${feature}`}
                        >
                            {ctaCopy} <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
